import React from 'react';
import {
  Box,
  Skeleton,
  Card,
  CardContent,
  Grid,
  Container,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  background: '#ffffff',
  border: '0 solid #e2e8f0',
  borderRadius: '12px',
  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  height: '100%',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.15), 0 2px 4px 0 rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)'
  }
}));

const MetricCardSkeleton = () => (
  <StyledCard>
    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
      {/* Icon and Title Row */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 2,
        mb: 2
      }}>
        <Skeleton 
          variant="rounded" 
          width={40} 
          height={40} 
          sx={{ 
            borderRadius: 2,
            bgcolor: '#f8fafc'
          }} 
        />
        <Skeleton 
          variant="text" 
          width="60%" 
          height={24} 
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }} 
        />
      </Box>
      
      {/* Value */}
      <Skeleton 
        variant="text" 
        width="80%" 
        height={48} 
        sx={{ 
          mb: 1,
          fontSize: { xs: '1.5rem', sm: '1.875rem' }
        }} 
      />

      {/* Subtitle */}
      <Skeleton 
        variant="text" 
        width="90%" 
        height={20} 
        sx={{ 
          fontSize: { xs: '0.75rem', sm: '0.875rem' }
        }} 
      />
    </CardContent>
  </StyledCard>
);

const ChartSkeleton = () => (
  <StyledCard>
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Chart Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Box>
          <Skeleton 
            variant="text" 
            width={200} 
            height={32} 
            sx={{ 
              mb: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }} 
          />
          <Skeleton 
            variant="text" 
            width={300} 
            height={20} 
            sx={{ 
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }} 
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Skeleton 
            variant="rounded" 
            width={100} 
            height={32} 
            sx={{ borderRadius: 2 }}
          />
          <Skeleton 
            variant="rounded" 
            width={100} 
            height={32} 
            sx={{ borderRadius: 2 }}
          />
        </Box>
      </Box>
      
      {/* Chart Legend */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[1, 2, 3].map((item) => (
          <Grid item xs={12} sm={4} key={item}>
            <Box sx={{ 
              textAlign: 'center', 
              p: 2,
              bgcolor: '#f8fafc',
              borderRadius: 2,
              border: '0 solid #e2e8f0'
            }}>
              <Skeleton 
                variant="text" 
                width="60%" 
                height={40} 
                sx={{ 
                  mx: 'auto', 
                  mb: 1,
                  fontSize: { xs: '1.5rem', sm: '1.875rem' }
                }} 
              />
              <Skeleton 
                variant="text" 
                width="80%" 
                height={20} 
                sx={{ 
                  mx: 'auto',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }} 
              />
            </Box>
          </Grid>
        ))}
      </Grid>
      
      {/* Chart Area */}
      <Skeleton 
        variant="rectangular" 
        height={{ xs: 250, sm: 300, md: 350 }} 
        sx={{ 
          borderRadius: 2,
          bgcolor: '#f8fafc'
        }} 
      />
    </CardContent>
  </StyledCard>
);

const SniffrBenefitsSkeleton = () => (
  <StyledCard>
    <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Skeleton 
        variant="text" 
        width={200} 
        height={32} 
        sx={{ 
          mb: 3,
          fontSize: { xs: '1rem', sm: '1.25rem' }
        }} 
      />
      <Skeleton 
        variant="text" 
        width="100%" 
        height={20} 
        sx={{ 
          mb: 3,
          fontSize: { xs: '0.75rem', sm: '0.875rem' }
        }} 
      />
      
      {/* Benefits Grid */}
      <Grid container spacing={3}>
        {[1, 2, 3].map((item) => (
          <Grid item xs={12} md={4} key={item}>
            <Box sx={{ 
              p: 3, 
              textAlign: 'center',
              bgcolor: '#f8fafc',
              borderRadius: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Skeleton 
                variant="circular" 
                width={32} 
                height={32} 
                sx={{ 
                  mb: 2,
                  bgcolor: '#e2e8f0'
                }} 
              />
              <Skeleton 
                variant="text" 
                width="70%" 
                height={24} 
                sx={{ 
                  mb: 1,
                  mx: 'auto',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }} 
              />
              <Skeleton 
                variant="text" 
                width="90%" 
                height={32} 
                sx={{ 
                  mb: 2,
                  mx: 'auto',
                  fontSize: { xs: '1.5rem', sm: '1.875rem' }
                }} 
              />
              <Skeleton 
                variant="text" 
                width="100%" 
                height={20} 
                sx={{ 
                  mb: 1,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }} 
              />
              <Skeleton 
                variant="text" 
                width="95%" 
                height={16} 
                sx={{ 
                  mb: 1,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }} 
              />
              <Skeleton 
                variant="text" 
                width="85%" 
                height={16} 
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }} 
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    </CardContent>
  </StyledCard>
);

const DashboardSkeleton = () => (
  <Box sx={{ 
    bgcolor: '#f8fafc', 
    minHeight: '100vh', 
    color: '#1a202c', 
    p: { xs: 2, sm: 3, md: 4 } 
  }}>
    <Container sx={{ maxWidth: { lg: "1000px", xl: "1200px" } }}>
      {/* Filters Skeleton */}
      <Box sx={{ 
        p: { xs: 2, sm: 3 }, 
        borderRadius: 2, 
        border: '0 solid #e2e8f0', 
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', 
        mb: { xs: 3, md: 4 },
        bgcolor: '#ffffff'
      }}>
        {/* Filter Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5, 
          mb: 3 
        }}>
          <Skeleton 
            variant="circular" 
            width={22} 
            height={22} 
            sx={{ bgcolor: '#e2e8f0' }}
          />
          <Skeleton 
            variant="text" 
            width={150} 
            height={28} 
            sx={{ 
              fontSize: { xs: '1.125rem', sm: '1.25rem' }
            }} 
          />
        </Box>
        
        {/* Filter Controls */}
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md key={item}>
              <Skeleton 
                variant="rounded" 
                height={40} 
                sx={{ 
                  borderRadius: 2,
                  bgcolor: '#f8fafc'
                }} 
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Engineering Health Skeleton */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Skeleton 
          variant="text" 
          width={200} 
          height={40} 
          sx={{ 
            mb: 2,
            fontSize: { xs: '1.125rem', sm: '1.25rem' }
          }} 
        />
        <Skeleton 
          variant="text" 
          width={300} 
          height={24} 
          sx={{ 
            mb: 3,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }} 
        />
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Grid item xs={12} sm={6} lg={4} xl={2} key={item}>
              <MetricCardSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Business Performance Skeleton */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Skeleton 
          variant="text" 
          width={200} 
          height={40} 
          sx={{ 
            mb: 2,
            fontSize: { xs: '1.125rem', sm: '1.25rem' }
          }} 
        />
        <Skeleton 
          variant="text" 
          width={300} 
          height={24} 
          sx={{ 
            mb: 3,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }} 
        />
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} lg={3} key={item}>
              <MetricCardSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Issue Detection Trends and Sniffr Benefits Skeleton */}
      <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ mt: 1 }}>
        <Grid item xs={12} lg={8}>
          <ChartSkeleton />
        </Grid>
        <Grid item xs={12} lg={4}>
          <SniffrBenefitsSkeleton />
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export {
  MetricCardSkeleton,
  ChartSkeleton,
  SniffrBenefitsSkeleton,
  DashboardSkeleton,
};
